/**
 * The map in the corner, and the world map behind it.
 *
 * Everything on the corner map is drawn from what the client already holds: the
 * region it was sent, the entities it is already drawing, the piles it already
 * knows about. Nothing here asks the server anything, so the map cannot lag
 * behind the world or tell anybody something the world would not.
 *
 * It turns with the camera by default, the way a map held in the hand turns
 * with the person holding it, with north marked on the rim so the turning does
 * not cost the sense of direction. Anybody who would rather have north stay up
 * can say so in the settings.
 */

import type { MapMarker, Waypoint } from './map-marks';
import type { PartyWire } from '@inverse/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { Game, MapMark } from '../game/game';
import { paintRegion, pointOnMap } from './map-paint';
import { PHASE_NAMES } from '@inverse/common';
import { CORNERS, CORNER_CURSORS, resizeSquare, roomFor, type Corner } from './resize';
import { useDraggableWindow } from './draggable';

/** How wide the corner map can be, and where it starts. */
const DEFAULT_SIZE = 168;
const SMALLEST = 120;
const BIGGEST = 340;

/** How many tiles it shows across, and how far that can be pushed either way. */
const DEFAULT_TILES = 30;
const CLOSEST = 14;
const FURTHEST = 70;

const ZOOM_KEY = 'inverse.minimap.tiles';
const SIZE_KEY = 'inverse.minimap.size';

/** How often the ground underneath is repainted, in milliseconds. */
const REPAINT_MS = 1500;

/**
 * How much ground is painted around the reader, in tiles each way.
 *
 * The corner map shows at most seventy tiles across, so this is comfortably
 * more than it can ever draw. Painting the whole region instead is what a
 * continent cannot afford: four million tiles of picture, rebuilt every second
 * and a half, is a frame dropped every few paces.
 */
const PAINTED_TILES = 128;

/**
 * How many pixels of the painted picture one tile gets.
 *
 * The corner map shows thirty tiles across two hundred pixels, so at one pixel
 * a tile every tile was drawn as a block six pixels square and the whole thing
 * read as a mosaic. Three carries the slope at three points across a tile
 * instead of one, which is what turns a staircase back into a hillside.
 */
const PAINTED_PER_TILE = 2;

/** What colour each sort of dot is. */
const DOT: Readonly<Record<MapMark['kind'], string>> = {
  player: '#ffffff',
  person: '#c8a86a',
  enemy: '#ff8b6b',
  item: '#e8c15a',
};

/** The primary colour, which is what marks the person holding the map. */
const YOU = '#0cd5e3';

/**
 * How far the map is turned, given where the camera is pointing.
 *
 * At the camera's resting yaw the map is north up. Everything else follows from
 * wanting whatever the camera is facing to be at the top, which is the only
 * arrangement where a thing seen ahead is a dot above the marker.
 */
function turnOf(yaw: number, north: boolean): number {
  return north ? 0 : yaw;
}

function readNumber(key: string, fallback: number, least: number, most: number): number {
  const held = Number(window.localStorage.getItem(key) ?? Number.NaN);
  return Number.isFinite(held) ? Math.min(Math.max(held, least), most) : fallback;
}

export function Minimap({
  game,
  northUp,
  marks,
  party,
  onOpenWorld,
}: {
  /**
   * The world itself, held rather than passed.
   *
   * A reference because the game outlives any one render and is made after the
   * first of them: a component given the value once would draw an empty map
   * forever, and one given it as state would re-render the whole interface the
   * moment the world started.
   */
  game: RefObject<Game | null>;
  /** Whether north stays at the top rather than the map turning with the camera. */
  northUp: boolean;
  /**
   * Where somebody is headed, and what they have written down.
   *
   * The same marks the map of everywhere draws. A waypoint is only any use if it
   * is on the map somebody actually looks at while walking.
   */
  marks: { waypoint: Waypoint | null; markers: MapMarker[] };
  /** Who is going with them, drawn as dots, and where the party is headed. */
  party: PartyWire;
  onOpenWorld: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /**
   * What is written down, read inside the drawing loop.
   *
   * The loop is started once and runs every frame; a value it closed over would
   * be the value it had when the map was first drawn, so a marker added later
   * would never appear.
   */
  const marksRef = useRef(marks);
  const partyRef = useRef(party);

  useEffect(() => {
    marksRef.current = marks;
  }, [marks]);

  useEffect(() => {
    partyRef.current = party;
  }, [party]);
  const ground = useRef<{
    canvas: HTMLCanvasElement;
    at: number;
    /** Which part of the region it is a picture of, in the region's own tiles. */
    from: { tx: number; tz: number; width: number; height: number };
  } | null>(null);

  const [tiles, setTiles] = useState(() => readNumber(ZOOM_KEY, DEFAULT_TILES, CLOSEST, FURTHEST));
  const [size, setSize] = useState(() => readNumber(SIZE_KEY, DEFAULT_SIZE, SMALLEST, BIGGEST));
  const [where, setWhere] = useState('');
  const [area, setArea] = useState('');
  const [time, setTime] = useState('');

  const home = useCallback(
    (box: { width: number; height: number }) => ({
      x: window.innerWidth - box.width - 14,
      y: 14,
    }),
    [],
  );

  const { attach, style, onPointerDown, nudge } = useDraggableWindow('minimap', { home });

  const zoom = (by: number): void => {
    const wanted = Math.min(Math.max(tiles + by, CLOSEST), FURTHEST);
    setTiles(wanted);
    window.localStorage.setItem(ZOOM_KEY, String(wanted));
  };

  /**
   * Dragging any corner makes it bigger or smaller, and it stays that way.
   *
   * The corner opposite whichever one is being pulled stays exactly where it
   * is, which for a window that can be anywhere on the screen means moving the
   * window as well as changing its size. The old grip assumed the map was
   * pinned to the top right corner, which stopped being true the first time
   * anybody dragged it somewhere else.
   */
  const resize =
    (corner: Corner) =>
    (event: React.PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();

      // The window itself, for working out how much room there is to grow into.
      const box = (event.currentTarget as HTMLElement).closest('.minimap');

      let last = { x: event.clientX, y: event.clientY };

      // The size is carried through the drag here rather than read back out of
      // state: an updater is not a place to move a window from, because React
      // is free to run one twice, and a nudge run twice moves twice as far.
      let side = size;

      const move = (moved: PointerEvent): void => {
        const dx = moved.clientX - last.x;
        const dy = moved.clientY - last.y;
        last = { x: moved.clientX, y: moved.clientY };

        // Never past the edge of the screen: a window that cannot move any
        // further stops growing rather than dragging its far corner along.
        const rect = box?.getBoundingClientRect();
        const room = rect
          ? roomFor({ x: rect.x, y: rect.y, width: rect.width, height: rect.height }, corner, {
              width: window.innerWidth,
              height: window.innerHeight,
            })
          : Infinity;

        const next = resizeSquare({ x: 0, y: 0, width: side, height: side }, corner, dx, dy, {
          least: SMALLEST,
          most: Math.min(BIGGEST, side + room),
        });

        if (next.width === side) return;
        side = next.width;

        setSize(side);
        // Whatever the resize did to the near corner, undo for the far one.
        if (next.x !== 0 || next.y !== 0) nudge(next.x, next.y);
      };

      const done = (): void => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', done);
        window.localStorage.setItem(SIZE_KEY, String(side));
      };

      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', done);
    };

  // Drawn on a frame of its own rather than on React's, because the dots move
  // whenever anything in the world moves and re-rendering a component sixty
  // times a second to move four dots would be a waste of the whole budget.
  useEffect(() => {
    let frame = 0;
    let said = '';
    let named = '';
    let phase = '';

    const draw = (): void => {
      frame = window.requestAnimationFrame(draw);

      const canvas = canvasRef.current;
      const view = game.current?.mapView() ?? null;
      if (!canvas || !view) return;

      const context = canvas.getContext('2d');
      if (!context) return;

      const now = performance.now();
      const local = {
        tx: Math.floor(view.where.x) - view.region.originX,
        tz: Math.floor(view.where.z) - view.region.originZ,
      };

      // Painted again when the clock says so, or when the reader has walked far
      // enough that the edge of what was painted is coming into view.
      const held = ground.current;
      const strayed =
        !held ||
        local.tx < held.from.tx + 24 ||
        local.tz < held.from.tz + 24 ||
        local.tx > held.from.tx + held.from.width - 24 ||
        local.tz > held.from.tz + held.from.height - 24;

      if (strayed || now - (held?.at ?? 0) > REPAINT_MS) {
        const from = {
          tx: local.tx - PAINTED_TILES / 2,
          tz: local.tz - PAINTED_TILES / 2,
          width: PAINTED_TILES,
          height: PAINTED_TILES,
        };

        const painted = paintRegion(view.region, from, PAINTED_PER_TILE);
        ground.current = { canvas: painted.canvas, at: now, from: painted };
      }

      const painted = ground.current;
      if (!painted) return;

      const middle = size / 2;
      const perTile = size / tiles;
      const turn = turnOf(view.yaw, northUp);
      const me = pointOnMap(view.region, view.where.x, view.where.z);

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, size, size);
      context.fillStyle = '#0b0d0c';
      context.fillRect(0, 0, size, size);

      context.save();
      context.translate(middle, middle);
      context.rotate(turn);
      context.scale(perTile, perTile);
      context.translate(-me.x, -me.y);

      // Smoothed, now there is something to smooth: at one pixel a tile this
      // had to be off or the map was a blur, and with three it is the last of
      // the staircase going.
      context.imageSmoothingEnabled = true;
      // The picture starts at the corner it was painted from, not at the corner
      // of the region, and is drawn at tile scale whatever it was painted at.
      context.drawImage(
        painted.canvas,
        painted.from.tx,
        painted.from.tz,
        painted.from.width,
        painted.from.height,
      );
      context.restore();

      // The dots are placed in the same turned frame so they sit exactly where
      // the ground under them does, but each is drawn square to the screen so a
      // square does not arrive as a diamond.
      const place = (x: number, z: number): { x: number; y: number } => {
        const point = pointOnMap(view.region, x, z);
        const dx = (point.x - me.x) * perTile;
        const dy = (point.y - me.y) * perTile;

        return {
          x: middle + dx * Math.cos(turn) - dy * Math.sin(turn),
          y: middle + dx * Math.sin(turn) + dy * Math.cos(turn),
        };
      };

      if (view.goal) {
        const at = place(view.goal.x, view.goal.z);
        context.strokeStyle = '#a8f0f7';
        context.lineWidth = 1.5;
        context.beginPath();
        context.moveTo(at.x - 4, at.y);
        context.lineTo(at.x + 4, at.y);
        context.moveTo(at.x, at.y - 4);
        context.lineTo(at.x, at.y + 4);
        context.stroke();
      }

      for (const mark of view.marks) {
        const at = place(mark.x, mark.z);
        if (at.x < 0 || at.y < 0 || at.x > size || at.y > size) continue;

        context.fillStyle = DOT[mark.kind];
        const dot = mark.kind === 'item' ? 2 : 3;
        context.fillRect(at.x - dot / 2, at.y - dot / 2, dot, dot);
      }

      // What somebody has written down, and where they are going. Drawn over
      // everything the world put there, because these are theirs.
      for (const marker of marksRef.current.markers) {
        if (marker.layer !== view.where.plane) continue;

        const at = place(marker.tx + 0.5, marker.tz + 0.5);
        if (at.x < 0 || at.y < 0 || at.x > size || at.y > size) continue;

        context.fillStyle = marker.colour;
        context.fillRect(at.x - 2, at.y - 2, 4, 4);
        context.strokeStyle = 'rgba(4, 20, 24, 0.9)';
        context.lineWidth = 1;
        context.strokeRect(at.x - 2.5, at.y - 2.5, 5, 5);
      }

      // Everybody going the same way, and where they are going. Drawn before
      // the waypoint so a mark somebody put down themselves sits on top.
      const together = partyRef.current;
      for (const member of together.members) {
        if (!member.near || member.layer !== view.where.plane) continue;
        const at = place(member.tx + 0.5, member.tz + 0.5);
        if (at.x < 0 || at.y < 0 || at.x > size || at.y > size) continue;

        context.fillStyle = '#c9a2ff';
        context.beginPath();
        context.arc(at.x, at.y, 2.6, 0, Math.PI * 2);
        context.fill();
        context.strokeStyle = 'rgba(4, 20, 24, 0.9)';
        context.lineWidth = 1;
        context.stroke();
      }

      if (together.point && together.point.layer === view.where.plane) {
        const at = place(together.point.tx + 0.5, together.point.tz + 0.5);
        const shown = {
          x: Math.min(Math.max(at.x, 6), size - 6),
          y: Math.min(Math.max(at.y, 6), size - 6),
        };
        context.strokeStyle = '#c9a2ff';
        context.lineWidth = 1.5;
        context.beginPath();
        context.arc(shown.x, shown.y, 4.5, 0, Math.PI * 2);
        context.stroke();
      }

      const waypoint = marksRef.current.waypoint;
      if (waypoint && waypoint.layer === view.where.plane) {
        const at = place(waypoint.tx + 0.5, waypoint.tz + 0.5);
        const held = {
          x: Math.min(Math.max(at.x, 6), size - 6),
          y: Math.min(Math.max(at.y, 6), size - 6),
        };

        // Held at the rim when it is off the map, so it still says which way to
        // walk rather than disappearing the moment it is worth knowing.
        context.strokeStyle = '#f2e3c0';
        context.lineWidth = 1.5;
        context.beginPath();
        context.moveTo(held.x - 4, held.y);
        context.lineTo(held.x + 4, held.y);
        context.moveTo(held.x, held.y - 4);
        context.lineTo(held.x, held.y + 4);
        context.stroke();

        context.beginPath();
        context.arc(held.x, held.y, 5.5, 0, Math.PI * 2);
        context.stroke();
      }

      // The character last, always in the middle, as a square in the primary
      // colour: the one mark on the map that is not something to go and find.
      context.fillStyle = YOU;
      context.fillRect(middle - 3, middle - 3, 6, 6);
      context.strokeStyle = '#04262a';
      context.lineWidth = 1;
      context.strokeRect(middle - 3.5, middle - 3.5, 7, 7);

      // North on the rim, which is what makes a turning map usable.
      const rim = middle - 10;
      context.fillStyle = '#cfd8d2';
      context.font = '600 10px system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('N', middle + Math.sin(turn) * rim, middle - Math.cos(turn) * rim);

      const at = `${Math.floor(view.where.x)}, ${Math.floor(view.where.z)}`;
      if (at !== said) {
        said = at;
        setWhere(at);
      }

      // What the ground is called, and the region's own name where nobody has
      // named it. A town is a more useful answer than an island.
      const here = view.called || view.region.name;
      if (here !== named) {
        named = here;
        setArea(named);
      }

      // And what stretch of the day it is, said where somebody is already
      // looking to find out where they are. Only when it changes, which is four
      // times an hour rather than sixty times a second.
      const stretch = view.time ? PHASE_NAMES[view.time] : '';
      if (stretch !== phase) {
        phase = stretch;
        setTime(phase);
      }
    };

    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [game, northUp, size, tiles]);

  /** Which tile a press landed on, undoing the turn and the zoom. */
  const tileAt = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ): { tx: number; tz: number } | null => {
    const view = game.current?.mapView();
    if (!view) return null;

    const box = event.currentTarget.getBoundingClientRect();
    const dx = event.clientX - box.left - box.width / 2;
    const dy = event.clientY - box.top - box.height / 2;

    const turn = turnOf(view.yaw, northUp);
    const perTile = box.width / tiles || 1;
    const mx = (dx * Math.cos(turn) + dy * Math.sin(turn)) / perTile;
    const my = (-dx * Math.sin(turn) + dy * Math.cos(turn)) / perTile;

    /*
     * Forwards, because the map is drawn forwards.
     *
     * Both axes used to be turned round here, from when the map was painted to
     * match a camera resting the other way. The painting was put the right way
     * up and this was not, so the map read correctly and answered a press with
     * the tile opposite the one pressed: clicking north of the marker walked
     * south of it, which is the sort of bug somebody blames on their own hand
     * the first three times.
     */
    return { tx: Math.floor(view.where.x + mx), tz: Math.floor(view.where.z + my) };
  };

  return (
    <div className="minimap" ref={attach} style={style}>
      <div className="minimap-head" onPointerDown={onPointerDown}>
        <span className="minimap-area">{area}</span>
        <span className="minimap-at">{time ? `${time} · ${where}` : where}</span>
      </div>

      <div className="minimap-frame">
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          title="Click to walk there"
          onPointerUp={(event) => {
            if (event.button !== 0) return;

            const tile = tileAt(event);
            if (tile) game.current?.walkToTile(tile.tx, tile.tz);
          }}
        />

        <div className="minimap-zoom">
          <button type="button" title="Closer" onClick={() => zoom(-4)} disabled={tiles <= CLOSEST}>
            +
          </button>
          <button
            type="button"
            title="Further out"
            onClick={() => zoom(4)}
            disabled={tiles >= FURTHEST}
          >
            &minus;
          </button>
          <button type="button" title="The world map (M)" onClick={onOpenWorld}>
            M
          </button>
        </div>

        {/* One on every corner: whichever is pulled, the far one stays put. */}
        {CORNERS.map((corner) => (
          <div
            key={corner}
            className={`minimap-grip ${corner}`}
            style={{ cursor: CORNER_CURSORS[corner] }}
            title="Drag to resize"
            onPointerDown={resize(corner)}
            aria-hidden
          />
        ))}
      </div>
    </div>
  );
}
